import 'whatwg-fetch'
import scrollIntoView from 'scroll-into-view'
import { Request } from '../lib/request'
import Pristine from '../lib/pristine'
import { whenDomReady } from '../lib/when-dom-ready'
import { findField, render as formialRender, SerializedForm, isSerializedInput } from 'formial'
import { fieldsWithValues, valueForField } from '../lib/serialize-form'
import '../lib/polyfill'
import { FormEvent } from 'react'

export interface ContactFormArgs {
    selector: string
    element?: HTMLElement
    baseUrl?: string
}

export class FormHandler {
    _formDefinition: any
    public _element: any

    public selector = ''
    public validation: any

    constructor({ selector, element }: ContactFormArgs) {
        if (element) {
            this._element = element
        }

        this.selector = selector
        whenDomReady(this.initialize)
    }

    get url(): string {
        return `/api/form/${this.formId}/embed.json`
    }

    get sucessEl() {
        return this.element.querySelector('#success-message')
    }

    get errorEl() {
        return this.element.querySelector('#error-message')
    }

    get formId() {
        return parseInt((document.querySelector('#form-id') as HTMLScriptElement).innerText)
    }

    get element() {
        return this._element || document.querySelector(this.selector)
    }

    get form() {
        return this.element.tagName === 'FORM' ? this.element : this.element.querySelector('form')
    }

    get submitBtn() {
        return this.form.querySelector('input[type="submit"]')
    }

    initialize = () => {
        const el = document.getElementById('form-contents')
        if (!el) throw new Error('unable to find selector #form-contents')

        formialRender(el, this.formDefinition)
        this.validation = new (Pristine as any)(this.form)
        this.form.addEventListener('submit', this.onSubmit)
    }

    get formDefinition(): SerializedForm {
        return (
            this._formDefinition ||
            ((this._formDefinition = JSON.parse(
                (document.getElementById('form-definition') as HTMLScriptElement).innerText
            )) as SerializedForm)
        )
    }

    onSubmit = async (ev: FormEvent<HTMLFormElement>) => {
        ev.preventDefault()
        if (this.validation.validate()) {
            try {
                await this.submit()
            } finally {
                if (this.submitBtn) {
                    this.submitBtn.disabled = false
                }
            }
        } else {
            this.form.classList.add('was-validated')
        }
    }

    get values() {
        const values = {}
        const fields = fieldsWithValues(this.form)
        const checkboxes = {}

        for (let i = 0; i < fields.length; i++) {
            const field = fields[i]
            const wrapper = field.closest('[data-control]')
            const attr = wrapper?.getAttribute('data-id')

            const def = wrapper && attr && findField(this.formDefinition, attr)
            const value = {
                id: field.id,
                name: field.name,
                type: field.type,
                value: valueForField(field),
            }
            if (!def || !isSerializedInput(def)) {
                continue
            }
            value['label'] = def.label
            if (field.type === 'checkbox') {
                const cb =
                    checkboxes[def.name] || (checkboxes[def.name] = { ...value, choices: [] })
                cb.choices.push({ id: field.name, label: field.value })
            } else {
                values[field.name] = value
            }
        }

        return { ...values, ...checkboxes }
    }

    async submit() {
        this.submitBtn.disabled = true
        this.errorEl.style.display = 'none'

        const status = await Request(this.url, {
            method: 'POST',
            json: { fields: this.values },
        })

        if (status.success) {
            this.displaySuccess()
        } else {
            this.errorEl.innerText = status.message
            this.errorEl.style.display = 'block'
        }
    }

    displaySuccess() {
        this.form.style.display = 'none'
        this.sucessEl.style.display = 'block'
    }

    scrollTo() {
        scrollIntoView(this.element)
    }
}
